import { useQuery } from '@tanstack/react-query'
import { Flex, Input, Modal, Table, TableColumnsType, Tag, Typography } from 'antd'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import LinkAddress from 'component/link/link-address'
import SelectPto from 'component/select/select-pto'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_MAX } from 'constants/pagination'
import useSearchParams from 'hooks/use-search-params'
import { logInfo } from 'utils/logger'

interface ReferralPerformanceItem {
  walletAddress: string
  ptoId: string
  ptoName: string
  volume: number
  earned: number
  earnedUsd: number
  rewardToken: number
  fromHash: string
  createdAt: string
}

const fetchReferralPerformance = async (params: {
  walletAddress: string
  ptoId?: string
  fromAddress?: string
  page?: number
  limit?: number
}) => {
  const response = await AppAdmin.getReferralPerformance(params)
  logInfo('ReferralPerformance', response.data.data)

  if (response.data.data) {
    return {
      data: response.data.data.results as ReferralPerformanceItem[],
      total: response.data.data.totalResults,
    }
  }

  return {
    data: [],
    total: 0,
  }
}

export const ModalReferralPerformance = ({
  walletAddress,
  onClose,
}: {
  walletAddress: string
  onClose: VoidFunction
}) => {
  const { searchParams, setSearchParams, initPagination, onChangePagination } = useSearchParams()
  const [ptoId, setPtoId] = useState<string | undefined>()
  const [search, setSearch] = useState<string>('')

  const { page, limit } = searchParams || {}

  const { data, isLoading } = useQuery({
    queryKey: ['referralPerformance', walletAddress, ptoId, search, page, limit],
    queryFn: () =>
      fetchReferralPerformance({
        walletAddress,
        ptoId,
        fromAddress: search.trim() || undefined,
        page,
        limit,
      }),
    gcTime: 30000,
  })

  const resetPage = () => {
    setSearchParams((prev) => ({ ...prev, page: DEFAULT_PAGE_INDEX }))
  }

  const handlePtoChange = (value: string | undefined) => {
    setPtoId(value)
    resetPage()
  }

  const handleWalletSearch = (value: string) => {
    setSearch(value.trim())
    resetPage()
  }

  const performance = data?.data || []

  const columns: TableColumnsType<ReferralPerformanceItem> = [
    {
      title: 'No.',
      dataIndex: 'fromHash',
      key: 'fromHash',
      render: (_: any, __: any, index: number) => {
        return (
          (Number(page || DEFAULT_PAGE_INDEX) - 1) * Number(limit || DEFAULT_PAGE_SIZE) + index + 1
        )
      },
      align: 'center',
    },
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      render: (address: string) => <LinkAddress address={address} />,
    },
    {
      title: 'PTO name',
      dataIndex: 'ptoName',
      key: 'ptoName',
    },
    {
      title: 'Volume (PTO)',
      dataIndex: 'volume',
      key: 'volume',
    },
    {
      title: 'Earned (MEY)',
      dataIndex: 'earned',
      key: 'earned',
      render: (earned: number) => {
        return <span>{new Intl.NumberFormat('en-US').format(earned || 0)}</span>
      },
    },
  ]

  return (
    <Modal
      open={!!walletAddress}
      centered
      width={700}
      destroyOnClose
      title={
        <Flex gap={12}>
          Referral Performance
          {!!data?.total && (
            <Tag color="blue" style={{ borderRadius: 8 }}>
              {data?.total}
            </Tag>
          )}
        </Flex>
      }
      onCancel={onClose}
      footer={null}
    >
      <div
        className="main-description"
        style={{
          marginBottom: 24,
        }}
      >
        View detailed referral activity and earnings for this user
      </div>
      <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
        <Typography.Text strong>Filter by PTO:</Typography.Text>
        <SelectPto
          value={ptoId}
          onChange={(value) => handlePtoChange(value as string | undefined)}
          style={{ flex: 1 }}
          placeholder="All PTO"
          pageSize={DEFAULT_PAGE_SIZE_MAX}
        />
      </Flex>
      <Flex align="center" gap={12} style={{ marginBottom: 16 }}>
        <Typography.Text strong>Wallet:</Typography.Text>
        <Input.Search
          placeholder="Search by wallet address"
          allowClear
          onSearch={handleWalletSearch}
          onChange={(e) => {
            if (!e.target.value) {
              handleWalletSearch('')
            }
          }}
          style={{ flex: 1 }}
        />
      </Flex>
      <Table
        columns={columns}
        loading={isLoading}
        dataSource={performance}
        rowKey={(record, index) => `${record.fromHash || ''}-${record.ptoId || ''}-${index}`}
        pagination={initPagination(data?.total)}
        onChange={onChangePagination}
        scroll={{ y: 320 }}
        bordered
      />
    </Modal>
  )
}
